// Admin mutations. Each hook calls the API directly via `getApiClient()`
// and patches the QueryClient cache. Server actions are not involved; this
// is the canonical mutation pattern for the admin surface.

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import type { components } from '@leanin/api-client';
import type { PlatformRole } from '@leanin/shared/views/profile';
import { getApiClient } from '../api-client';
import { throwApiError } from '../api-error';
import { adminKeys } from '../queries/admin';
import {
  broadcastKeys,
  type AdminBroadcastDetail,
  type BroadcastDoc,
  type BroadcastPreview,
  type BroadcastScope,
} from '../queries/broadcasts';

type AudienceSpec = components['schemas']['AudienceSpec'];
type SendVia = 'email' | 'email_in_app';

// ─── Members ────────────────────────────────────────────────────────────

function invalidateMembersAndDetail(
  qc: ReturnType<typeof useQueryClient>,
  userId?: string,
) {
  qc.invalidateQueries({ queryKey: adminKeys.members() });
  if (userId !== undefined) {
    qc.invalidateQueries({ queryKey: adminKeys.memberDetail(userId) });
  }
}

export function useDeactivateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; reason: string }) => {
      const client = getApiClient();
      const { error } = await client.POST(
        '/api/v1/admin/members/{id}/deactivate',
        {
          params: { path: { id: input.userId } },
          body: { reason: input.reason },
        },
      );
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: (_data, vars) => invalidateMembersAndDetail(qc, vars.userId),
  });
}

export function useReactivateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; reason: string }) => {
      const client = getApiClient();
      const { error } = await client.POST(
        '/api/v1/admin/members/{id}/reactivate',
        {
          params: { path: { id: input.userId } },
          body: { reason: input.reason },
        },
      );
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: (_data, vars) => invalidateMembersAndDetail(qc, vars.userId),
  });
}

export function useBanMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; reason: string }) => {
      const client = getApiClient();
      const { error } = await client.POST('/api/v1/admin/members/{id}/ban', {
        params: { path: { id: input.userId } },
        body: { reason: input.reason },
      });
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: (_data, vars) => invalidateMembersAndDetail(qc, vars.userId),
  });
}

export function useSetMemberPlatformRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      userId: string;
      platformRole: PlatformRole | null;
    }) => {
      const client = getApiClient();
      const { error } = await client.POST(
        '/api/v1/admin/members/{id}/platform-role',
        {
          params: { path: { id: input.userId } },
          body: { platformRole: input.platformRole },
        },
      );
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: (_data, vars) => invalidateMembersAndDetail(qc, vars.userId),
  });
}

export function useSetColdStartSeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; enabled: boolean }) => {
      const client = getApiClient();
      const { error } = await client.POST(
        '/api/v1/admin/members/{id}/cold-start-seed',
        {
          params: { path: { id: input.userId } },
          body: { enabled: input.enabled },
        },
      );
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: (_data, vars) => invalidateMembersAndDetail(qc, vars.userId),
  });
}

export interface UpdateMemberProfileInput {
  readonly userId: string;
  readonly firstName?: string;
  readonly lastName?: string | null;
  readonly email?: string;
  readonly bio?: string | null;
  readonly jobTitle?: string | null;
  readonly company?: string | null;
  readonly industry?: string | null;
  /** Structured place payload from `PlacePicker`. `null` clears any prior
   *  selection. */
  readonly place?: {
    placeId: string;
    placeText: string;
    placeLat: number;
    placeLng: number;
    placeCountryCode: string | null;
  } | null;
  readonly secondaryEmail?: string | null;
  readonly timezone?: string | null;
}

export function useUpdateMemberProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateMemberProfileInput) => {
      const { userId, ...rest } = input;
      const client = getApiClient();
      const { error } = await client.PATCH(
        '/api/v1/admin/members/{id}/profile',
        {
          params: { path: { id: userId } },
          body: rest,
        },
      );
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: (_data, vars) => invalidateMembersAndDetail(qc, vars.userId),
  });
}

export function useBulkSetMemberPlatformRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      userIds: ReadonlyArray<string>;
      platformRole: PlatformRole | null;
    }) => {
      const client = getApiClient();
      const { data, error } = await client.POST(
        '/api/v1/admin/members/bulk/platform-role',
        {
          body: {
            userIds: [...input.userIds],
            platformRole: input.platformRole,
          },
        },
      );
      if (error !== undefined || data === undefined) throwApiError(error);
      return {
        successCount: data.successCount,
        failureCount: data.failures.length,
      };
    },
    onSuccess: () => invalidateMembersAndDetail(qc),
  });
}

export function useBulkAddToCircle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      userIds: ReadonlyArray<string>;
      circleId: string;
    }) => {
      const client = getApiClient();
      const { data, error } = await client.POST(
        '/api/v1/admin/members/bulk/circle',
        {
          body: { userIds: [...input.userIds], circleId: input.circleId },
        },
      );
      if (error !== undefined || data === undefined) throwApiError(error);
      return {
        successCount: data.successCount,
        failureCount: data.failures.length,
      };
    },
    onSuccess: () => invalidateMembersAndDetail(qc),
  });
}

export function useBulkDeactivate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      userIds: ReadonlyArray<string>;
      reason: string;
    }) => {
      const client = getApiClient();
      const { data, error } = await client.POST(
        '/api/v1/admin/members/bulk/deactivate',
        { body: { userIds: [...input.userIds], reason: input.reason } },
      );
      if (error !== undefined || data === undefined) throwApiError(error);
      return {
        successCount: data.successCount,
        failureCount: data.failureCount,
      };
    },
    onSuccess: () => invalidateMembersAndDetail(qc),
  });
}

export function useBulkSendEmail() {
  return useMutation({
    mutationFn: async (input: {
      userIds: ReadonlyArray<string>;
      subject: string;
      bodyMarkdown: string;
    }) => {
      const client = getApiClient();
      const { data, error } = await client.POST(
        '/api/v1/admin/members/bulk/email',
        {
          body: {
            userIds: [...input.userIds],
            subject: input.subject,
            body: input.bodyMarkdown,
          },
        },
      );
      if (error !== undefined || data === undefined) throwApiError(error);
      return {
        draftId: data.draftId,
        recipientCount: data.recipientCount,
      };
    },
  });
}

export function useAddMemberToCircle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; circleId: string }) => {
      const client = getApiClient();
      const { error } = await client.POST(
        '/api/v1/admin/members/{userId}/circles',
        {
          params: { path: { userId: input.userId } },
          body: { circleId: input.circleId },
        },
      );
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: (_data, vars) => invalidateMembersAndDetail(qc, vars.userId),
  });
}

export function useRemoveMemberFromCircle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; circleId: string }) => {
      const client = getApiClient();
      const { error } = await client.DELETE(
        '/api/v1/admin/members/{userId}/circles/{circleId}',
        {
          params: {
            path: { userId: input.userId, circleId: input.circleId },
          },
        },
      );
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: (_data, vars) => invalidateMembersAndDetail(qc, vars.userId),
  });
}

export function useAddMemberToNetwork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; networkId: string }) => {
      const client = getApiClient();
      const { error } = await client.POST(
        '/api/v1/admin/members/{userId}/networks',
        {
          params: { path: { userId: input.userId } },
          body: { networkId: input.networkId },
        },
      );
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: (_data, vars) => invalidateMembersAndDetail(qc, vars.userId),
  });
}

export function useRemoveMemberFromNetwork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; networkId: string }) => {
      const client = getApiClient();
      const { error } = await client.DELETE(
        '/api/v1/admin/members/{userId}/networks/{networkId}',
        {
          params: {
            path: { userId: input.userId, networkId: input.networkId },
          },
        },
      );
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: (_data, vars) => invalidateMembersAndDetail(qc, vars.userId),
  });
}

export type PresignPhotoResult = {
  uploadUrl: string;
  fields: Record<string, string>;
  key: string;
};

export function usePresignMemberPhoto() {
  return useMutation({
    mutationFn: async (input: {
      userId: string;
      contentType: 'image/jpeg' | 'image/png' | 'image/webp';
    }): Promise<PresignPhotoResult> => {
      const client = getApiClient();
      const { data, error } = await client.POST(
        '/api/v1/admin/members/{id}/photo',
        {
          params: { path: { id: input.userId } },
          body: { contentType: input.contentType },
        },
      );
      if (error !== undefined || data === undefined) throwApiError(error);
      return {
        uploadUrl: data.uploadUrl,
        fields: data.fields,
        key: data.key,
      };
    },
  });
}

export function useCommitMemberPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; storageKey: string }) => {
      const client = getApiClient();
      const { error } = await client.POST(
        '/api/v1/admin/members/{id}/photo/commit',
        {
          params: { path: { id: input.userId } },
          body: { storageKey: input.storageKey },
        },
      );
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: (_data, vars) => invalidateMembersAndDetail(qc, vars.userId),
  });
}

export function useSendPasswordResetEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const client = getApiClient();
      const { error } = await client.POST(
        '/api/v1/admin/members/{id}/password-reset',
        { params: { path: { id: userId } } },
      );
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: (_data, userId) =>
      invalidateMembersAndDetail(qc, userId),
  });
}

// ─── Invitations ─────────────────────────────────────────────────────────

export interface CreateInvitationInput {
  email: string;
  firstName: string;
  lastName: string;
  /** Optional platform grant applied at acceptance time. Omit (or pass
   *  `null`) to invite a regular user. */
  platformRole?: PlatformRole | null;
  addToCircleId?: string;
  addToNetworkId?: string;
  jobTitle?: string;
  company?: string;
  personalNote?: string;
}

export function useCreateInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateInvitationInput) => {
      const client = getApiClient();
      const { error } = await client.POST('/api/v1/admin/invitations', {
        body: {
          email: input.email,
          firstName: input.firstName,
          lastName: input.lastName,
          ...(input.platformRole !== undefined
            ? { platformRole: input.platformRole }
            : {}),
          ...(input.addToCircleId !== undefined
            ? { addToCircleId: input.addToCircleId }
            : {}),
          ...(input.addToNetworkId !== undefined
            ? { addToNetworkId: input.addToNetworkId }
            : {}),
          ...(input.jobTitle !== undefined ? { jobTitle: input.jobTitle } : {}),
          ...(input.company !== undefined ? { company: input.company } : {}),
          ...(input.personalNote !== undefined
            ? { personalNote: input.personalNote }
            : {}),
        },
      });
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

export function useRevokeInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invitationId: string) => {
      const client = getApiClient();
      const { error } = await client.POST(
        '/api/v1/admin/invitations/{id}/revoke',
        { params: { path: { id: invitationId } } },
      );
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

export function useResendInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invitationId: string) => {
      const client = getApiClient();
      const { error } = await client.POST(
        '/api/v1/admin/invitations/{id}/resend',
        { params: { path: { id: invitationId } } },
      );
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

export interface BulkInviteCreatedRow {
  email: string;
  firstName: string | null;
  lastName: string | null;
  invitationId: string;
}

export interface BulkInviteSkippedRow {
  row: number;
  email: string;
  reason: string;
}

export function useBulkInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (csv: string) => {
      const client = getApiClient();
      const { data, error } = await client.POST(
        '/api/v1/admin/invitations/bulk',
        { body: { csv } },
      );
      if (error !== undefined || data === undefined) throwApiError(error);
      return {
        created: data.invitations.map(
          (r): BulkInviteCreatedRow => ({
            email: r.email,
            firstName: r.firstName ?? null,
            lastName: r.lastName ?? null,
            invitationId: r.invitationId,
          }),
        ),
        skipped: data.skipped.map(
          (r): BulkInviteSkippedRow => ({
            row: r.row,
            email: r.email,
            reason: r.reason,
          }),
        ),
      };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

// ─── Broadcasts ──────────────────────────────────────────────────────────
//
// Two URL families:
//   • Per-scope create + list + detail under `/admin/broadcasts` (platform)
//     and `/circles|networks|spaces/{id}/broadcasts` (groups). Scope IS the
//     lookup key, so create stays scope-aware here.
//   • Canonical operations on an existing row — PATCH, /preview, DELETE,
//     /duplicate — live at `/api/v1/broadcasts/{id}` regardless of scope.
//     Path B authz resolves scope from the broadcast row.
//
// Cache invalidation (Frozen Decision 11): every mutation that touches an
// existing row writes the detail back via `setQueryData(detail(scope, id))`
// then invalidates the list + statusCounts prefixes for that scope. For
// platform-only hooks scope is `{kind: 'platform'}`; the group hooks live in
// mutations/groups.ts and carry kind/groupId so they can target the right
// scope key.

export interface CreateBroadcastInput {
  sendVia: SendVia;
  subject: string;
  bodyDoc: BroadcastDoc;
  preheaderText?: string | null;
  replyTo?: string | null;
  audienceSpec: AudienceSpec;
  scheduledSendAt?: string;
  mode: 'send' | 'schedule' | 'draft' | 'test';
}

// Prefix that matches every cached list for a scope regardless of params.
// `broadcastKeys.list(scope, _)` is `[...scope, 'list', params]` — strip
// the trailing params object to invalidate "any list under this scope".
function listPrefix(scope: BroadcastScope) {
  return [...broadcastKeys.scope(scope), 'list'] as const;
}

const PLATFORM_SCOPE: BroadcastScope = { kind: 'platform' };

function invalidatePlatformBroadcasts(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: listPrefix(PLATFORM_SCOPE) });
  qc.invalidateQueries({
    queryKey: broadcastKeys.statusCounts(PLATFORM_SCOPE),
  });
}

export function useCreateBroadcast() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateBroadcastInput) => {
      const client = getApiClient();
      if (input.mode === 'schedule' && input.scheduledSendAt !== undefined) {
        const { error } = await client.POST('/api/v1/admin/broadcasts', {
          body: {
            audienceSpec: input.audienceSpec,
            sendVia: input.sendVia,
            subject: input.subject,
            bodyDoc: input.bodyDoc,
            ...(input.preheaderText !== undefined
              ? { preheaderText: input.preheaderText }
              : {}),
            ...(input.replyTo !== undefined ? { replyTo: input.replyTo } : {}),
            scheduledSendAt: input.scheduledSendAt,
          },
        });
        if (error !== undefined) throwApiError(error);
        return;
      }
      // draft / send / test all land via the same create endpoint;
      // the dispatcher picks up sent rows separately.
      const { error } = await client.POST('/api/v1/admin/broadcasts/create', {
        body: {
          audienceSpec: input.audienceSpec,
          sendVia: input.sendVia,
          subject: input.subject,
          bodyDoc: input.bodyDoc,
          ...(input.preheaderText !== undefined
            ? { preheaderText: input.preheaderText }
            : {}),
          ...(input.replyTo !== undefined ? { replyTo: input.replyTo } : {}),
        },
      });
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: () => {
      // Legacy admin-keyed broadcast caches still exist for the platform
      // list page (queries/admin.ts `useAdminBroadcasts`). Invalidate both
      // surfaces until that page migrates to the scope-aware reader.
      qc.invalidateQueries({ queryKey: adminKeys.broadcasts() });
      invalidatePlatformBroadcasts(qc);
    },
  });
}

export interface UpdateBroadcastInput {
  version: number;
  sendVia?: SendVia;
  subject?: string;
  bodyDoc?: BroadcastDoc;
  preheaderText?: string | null;
  replyTo?: string | null;
  audienceSpec?: AudienceSpec;
  scheduledSendAt?: string | null;
}

export function useUpdateBroadcast(): UseMutationResult<
  AdminBroadcastDetail,
  Error,
  { id: string; patch: UpdateBroadcastInput }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }) => {
      const client = getApiClient();
      const { data, error } = await client.PATCH(
        '/api/v1/broadcasts/{id}',
        {
          params: { path: { id } },
          body: {
            version: patch.version,
            ...(patch.sendVia !== undefined ? { sendVia: patch.sendVia } : {}),
            ...(patch.subject !== undefined ? { subject: patch.subject } : {}),
            ...(patch.bodyDoc !== undefined ? { bodyDoc: patch.bodyDoc } : {}),
            ...(patch.preheaderText !== undefined
              ? { preheaderText: patch.preheaderText }
              : {}),
            ...(patch.replyTo !== undefined ? { replyTo: patch.replyTo } : {}),
            ...(patch.audienceSpec !== undefined
              ? { audienceSpec: patch.audienceSpec }
              : {}),
            ...(patch.scheduledSendAt !== undefined
              ? { scheduledSendAt: patch.scheduledSendAt }
              : {}),
          },
        },
      );
      if (error !== undefined || data === undefined) throwApiError(error);
      return data.data;
    },
    onSuccess: (data, vars) => {
      qc.setQueryData(broadcastKeys.detail(PLATFORM_SCOPE, vars.id), data);
      invalidatePlatformBroadcasts(qc);
      // Keep the legacy admin-broadcasts list (queries/admin.ts) in sync
      // until that surface migrates to the scope-aware reader.
      qc.invalidateQueries({ queryKey: adminKeys.broadcasts() });
    },
  });
}

export interface PreviewBroadcastInput {
  id: string;
  bodyDoc?: BroadcastDoc;
  subject?: string;
  preheaderText?: string | null;
  firstName?: string;
}

export function usePreviewBroadcast(): UseMutationResult<
  BroadcastPreview,
  Error,
  PreviewBroadcastInput
> {
  return useMutation({
    mutationFn: async (input) => {
      const client = getApiClient();
      const { data, error } = await client.POST(
        '/api/v1/broadcasts/{id}/preview',
        {
          params: { path: { id: input.id } },
          body: {
            ...(input.bodyDoc !== undefined ? { bodyDoc: input.bodyDoc } : {}),
            ...(input.subject !== undefined ? { subject: input.subject } : {}),
            ...(input.preheaderText !== undefined
              ? { preheaderText: input.preheaderText }
              : {}),
            ...(input.firstName !== undefined
              ? { firstName: input.firstName }
              : {}),
          },
        },
      );
      if (error !== undefined || data === undefined) throwApiError(error);
      return data.data;
    },
  });
}

export function useDuplicateBroadcast(): UseMutationResult<
  { id: string },
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (broadcastId) => {
      const client = getApiClient();
      const { data, error } = await client.POST(
        '/api/v1/broadcasts/{id}/duplicate',
        { params: { path: { id: broadcastId } } },
      );
      if (error !== undefined || data === undefined) throwApiError(error);
      return { id: data.id };
    },
    onSuccess: () => {
      // The duplicated row's scope matches the source row's scope, which the
      // caller doesn't pass through this signature. Blanket-invalidate every
      // broadcast list/statusCounts entry so the new row appears wherever
      // it lives.
      qc.invalidateQueries({ queryKey: broadcastKeys.all });
      qc.invalidateQueries({ queryKey: adminKeys.broadcasts() });
    },
  });
}

export function useDeleteBroadcast(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (broadcastId) => {
      const client = getApiClient();
      const { error } = await client.DELETE(
        '/api/v1/broadcasts/{id}',
        { params: { path: { id: broadcastId } } },
      );
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: (_data, broadcastId) => {
      // Drop the now-gone broadcast's detail cache entirely (across every
      // scope variant the key cartesian could hold) so the detail view's
      // useQuery doesn't refetch a 404 after the row's been deleted.
      // `removeQueries` evicts the cache without triggering a refetch;
      // `invalidateQueries` would re-fire and log a 404 in the console.
      qc.removeQueries({
        predicate: (q) =>
          q.queryKey[0] === 'broadcasts' &&
          q.queryKey.includes('detail') &&
          q.queryKey.includes(broadcastId),
      });
      // Lists + status-counts across every scope DO need re-fetching.
      qc.invalidateQueries({ queryKey: broadcastKeys.all });
      qc.invalidateQueries({ queryKey: adminKeys.broadcasts() });
    },
  });
}

export function useRescheduleBroadcast(): UseMutationResult<
  void,
  Error,
  { id: string; scheduledAt: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, scheduledAt }) => {
      const client = getApiClient();
      const { error } = await client.POST(
        '/api/v1/admin/broadcasts/{id}/schedule',
        {
          params: { path: { id } },
          body: { scheduledAt },
        },
      );
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: () => {
      // Detail timestamp + status pill + list ordering all read off the
      // broadcast row, so invalidate the union.
      qc.invalidateQueries({ queryKey: broadcastKeys.all });
      qc.invalidateQueries({ queryKey: adminKeys.broadcasts() });
    },
  });
}

// ─── Moderation ──────────────────────────────────────────────────────────

type ContentAction = 'manual_hide' | 'delete' | 'release';
type MemberAction = 'warn' | 'suspend' | 'reactivate';

function invalidateModerationFor(
  qc: ReturnType<typeof useQueryClient>,
  reportId?: string,
) {
  qc.invalidateQueries({ queryKey: adminKeys.moderation() });
  if (reportId !== undefined) {
    qc.invalidateQueries({ queryKey: adminKeys.moderationReport(reportId) });
  }
}

export function useApplyContentAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      reportId: string;
      action: ContentAction;
      reason: string;
      notifyReporter: boolean;
    }) => {
      const client = getApiClient();
      const { error } = await client.POST(
        '/api/v1/admin/moderation/reports/{id}/action',
        {
          params: { path: { id: input.reportId } },
          body: {
            action: input.action,
            reason: input.reason,
            notifyReporter: input.notifyReporter,
          },
        },
      );
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: (_data, vars) => invalidateModerationFor(qc, vars.reportId),
  });
}

export function useApplyMemberAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      reportId: string;
      action: MemberAction;
      reason: string;
      notifyReporter: boolean;
    }) => {
      const client = getApiClient();
      const { error } = await client.POST(
        '/api/v1/admin/moderation/reports/{id}/action',
        {
          params: { path: { id: input.reportId } },
          body: {
            action: input.action,
            reason: input.reason,
            notifyReporter: input.notifyReporter,
          },
        },
      );
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: (_data, vars) => invalidateModerationFor(qc, vars.reportId),
  });
}

export function useDismissReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { reportId: string; notes?: string }) => {
      const client = getApiClient();
      const { error } = await client.POST(
        '/api/v1/admin/moderation/reports/{id}/dismiss',
        {
          params: { path: { id: input.reportId } },
          body: {
            ...(input.notes !== undefined ? { notes: input.notes } : {}),
          },
        },
      );
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: (_data, vars) => invalidateModerationFor(qc, vars.reportId),
  });
}

export function useEscalateReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { reportId: string; note: string }) => {
      const client = getApiClient();
      const { error } = await client.POST(
        '/api/v1/admin/moderation/reports/{id}/escalate',
        {
          params: { path: { id: input.reportId } },
          body: { note: input.note },
        },
      );
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: (_data, vars) => invalidateModerationFor(qc, vars.reportId),
  });
}

export function useConfirmHold() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (holdId: string) => {
      const client = getApiClient();
      const { error } = await client.POST(
        '/api/v1/admin/moderation/holds/{id}/confirm',
        { params: { path: { id: holdId } } },
      );
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: () => invalidateModerationFor(qc),
  });
}

export function useReleaseHold() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { holdId: string; reason: string }) => {
      const client = getApiClient();
      const { error } = await client.POST(
        '/api/v1/admin/moderation/holds/{id}/release',
        {
          params: { path: { id: input.holdId } },
          body: { reason: input.reason },
        },
      );
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: () => invalidateModerationFor(qc),
  });
}

export function useFinalizeDeleteHold() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { holdId: string; reason: string }) => {
      const client = getApiClient();
      const { error } = await client.POST(
        '/api/v1/admin/moderation/holds/{id}/finalize-delete',
        {
          params: { path: { id: input.holdId } },
          body: { reason: input.reason },
        },
      );
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: () => invalidateModerationFor(qc),
  });
}

export function useResolveContestation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      contestationId: string;
      outcome: 'upheld' | 'overturned';
      reason: string;
    }) => {
      const client = getApiClient();
      const { error } = await client.POST(
        '/api/v1/admin/moderation/contestations/{id}/resolve',
        {
          params: { path: { id: input.contestationId } },
          body: { outcome: input.outcome, reason: input.reason },
        },
      );
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: () => invalidateModerationFor(qc),
  });
}

// ─── Resources (admin lifecycle) ─────────────────────────────────────────

function invalidateResources(
  qc: ReturnType<typeof useQueryClient>,
  resourceId?: string,
) {
  qc.invalidateQueries({ queryKey: adminKeys.resources() });
  if (resourceId !== undefined) {
    qc.invalidateQueries({ queryKey: adminKeys.resourceDetail(resourceId) });
  }
}

export function usePublishResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (resourceId: string) => {
      const client = getApiClient();
      const { error } = await client.POST(
        '/api/v1/resources/{id}/publish',
        { params: { path: { id: resourceId } } },
      );
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: (_data, resourceId) => invalidateResources(qc, resourceId),
  });
}

export function useUnpublishResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (resourceId: string) => {
      const client = getApiClient();
      const { error } = await client.POST(
        '/api/v1/resources/{id}/unpublish',
        { params: { path: { id: resourceId } } },
      );
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: (_data, resourceId) => invalidateResources(qc, resourceId),
  });
}

export function useFeatureResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (resourceId: string) => {
      const client = getApiClient();
      const { error } = await client.POST(
        '/api/v1/admin/resources/{id}/feature',
        { params: { path: { id: resourceId } } },
      );
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: (_data, resourceId) => invalidateResources(qc, resourceId),
  });
}

export function useUnfeatureResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (resourceId: string) => {
      const client = getApiClient();
      const { error } = await client.DELETE(
        '/api/v1/admin/resources/{id}/feature',
        { params: { path: { id: resourceId } } },
      );
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: (_data, resourceId) => invalidateResources(qc, resourceId),
  });
}

export function useDeleteResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (resourceId: string) => {
      const client = getApiClient();
      const { error } = await client.DELETE('/api/v1/resources/{id}', {
        params: { path: { id: resourceId } },
      });
      if (error !== undefined) throwApiError(error);
    },
    onSuccess: () => invalidateResources(qc),
  });
}
